import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Button, Dialog, Field, Input, Textarea } from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';
import { number } from '../../lib/format.js';

/**
 * Putting credits on an account, or taking them off.
 *
 * One dialog for both sides of the market: an agency is named by its broker
 * id, a tenant or owner by their user id, and the server writes to the same
 * ledger either way. Sharing it is what keeps the wording, the confirmation
 * and the audit trail the same wherever an admin reaches for it.
 *
 * A signed amount rather than an "add" and a "remove" button: the ledger
 * records a single signed movement, and two buttons would be two ways of
 * saying the same thing with one of them inevitably worded differently.
 */
export function useCreditAdjustment() {
  const queryClient = useQueryClient();

  const [target, setTarget] = useState(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [isWorking, setIsWorking] = useState(false);

  const close = () => {
    setTarget(null);
    setAmount('');
    setReason('');
  };

  /*
   * Typed, so it arrives as text. A blank box is not zero, "12x" is not 12,
   * and the endpoint refuses a zero adjustment — better to say so before the
   * round trip than to render its error.
   */
  const parsed = Number(String(amount).trim());
  const isValidAmount =
    String(amount).trim() !== '' && Number.isInteger(parsed) && parsed !== 0;

  const submit = async () => {
    setIsWorking(true);

    try {
      const result = await api.finance.adjustCredits({
        ...(target.brokerId ? { brokerId: target.brokerId } : { userId: target.userId }),
        amount: parsed,
        reason: reason.trim(),
      });

      toast.success(
        `${parsed > 0 ? 'Added' : 'Removed'} ${number(Math.abs(parsed))} credits. ${
          target.name || 'The account'
        } now holds ${number(result.balanceAfter)}.`,
      );

      close();
      // Every screen that shows a balance: the account, its list, and the
      // ledger this movement now appears in.
      queryClient.invalidateQueries({ queryKey: ['admin-user'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-broker'] });
      queryClient.invalidateQueries({ queryKey: ['admin-brokers'] });
      queryClient.invalidateQueries({ queryKey: ['admin-credit-ledger'] });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsWorking(false);
    }
  };

  const dialog = (
    <Dialog
      open={Boolean(target)}
      onClose={close}
      title={`Adjust credits for ${target?.name || 'this account'}?`}
      warning="The balance changes immediately and the movement is written to the credit ledger as an adjustment, against your account. Credits already spent are not revisited."
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={isWorking}
            disabled={!isValidAmount || reason.trim().length < 3}
          >
            {parsed < 0 ? 'Remove credits' : 'Add credits'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field
          label="Credits"
          hint="A whole number. Negative takes credits off the account."
          id="adjust-amount"
        >
          <Input
            id="adjust-amount"
            type="text"
            inputMode="numeric"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="50"
          />
        </Field>

        <Field label="Reason" hint="Recorded in the ledger and the audit log." id="adjust-reason">
          <Textarea
            id="adjust-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why is this adjustment being made?"
          />
        </Field>
      </div>
    </Dialog>
  );

  return {
    dialog,
    /** `{ userId }` or `{ brokerId }`, plus a name for the copy. */
    ask: (next) => {
      setAmount('');
      setReason('');
      setTarget(next);
    },
  };
}

export default useCreditAdjustment;
